import {useState, FC, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../text';
import {utils} from '../utils';
import {hooks} from '../hooks';
import {theme} from '../constants';
import {components} from '../components';
import Https from '../utils/apiUtility';
import axios from 'axios';

const API_URL = "http://localhost:3000";

export const ConfirmationCode: FC = () => {
  const { pathname, state } = useLocation() as { pathname: string; state?: { email?: string } };
  const navigate = hooks.useAppNavigate();

  const [inputs, setInputs] = useState<string[]>(new Array(6).fill(''));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const email = state?.email || localStorage.getItem('user_email');

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    setTimeout(() => {
      window.scroll({top: -1, left: 0, behavior: 'smooth'});
    }, 10);
  }, [pathname]);

  const renderBackground = (): JSX.Element => {
    return <components.Background />;
  };


  const handleOtpVerification = async () => {
    const otpCode = inputs.join('');

    if (!email) {
      setErrorMessage('E-mail não encontrado. Retorne à tela de login.');
      return;
    }

    if (otpCode.length !== 6 || inputs.includes('')) {
      setErrorMessage('Preencha todos os campos corretamente.');
      return;
    }

    try {
      setErrorMessage(null);
      console.log('Enviando OTP para verificação...', { identifier: email, type: 'email', otp: otpCode });

      const response = await axios.post(`${API_URL}/auth/verify-otp`, {
        identifier: email,
        type: 'email',
        otp: otpCode
      });

      console.log('Resposta do backend:', response);

      if (response.status === 201) {
        const { access_token, refresh_token } = response.data;
        localStorage.setItem('jwt_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        navigate('/TabNavigator');
      } else {
        setErrorMessage('Código OTP incorreto.');
      }
    } catch (error: any) {
      console.error('Erro na verificação do OTP:', error);

      if (error.response) {
        console.error('Erro da API:', error.response);
        setErrorMessage('Código OTP incorreto.');
      } else {
        setErrorMessage('Erro inesperado ao verificar código OTP.');
      }
    }
  };


  const handleResendOtp = async () => {
    if (!email) {
      setErrorMessage('E-mail não encontrado. Retorne à tela de login.');
      return;
    }

    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      console.log('Reenviando OTP para:', email);

      const response = await axios.post(`${API_URL}/auth/request-otp`, {
        identifier: email,
        type: 'email'
      });

      console.log('Resposta do backend:', response);

      if (response.status === 201) {
        setSuccessMessage('Código OTP reenviado com sucesso!');
      } else {
        setErrorMessage('Erro ao reenviar código OTP.');
      }
    } catch (error: any) {
      console.error('Erro ao reenviar OTP:', error);

      if (error.response) {
        console.error('Erro da API:', error.response);
        setErrorMessage('Erro ao reenviar código OTP.');
      } else {
        setErrorMessage('Erro inesperado ao reenviar código OTP.');
      }
    }
  };


  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return; // Permite apenas números

    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);

    // Move para o próximo campo se o usuário digitou um número
    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !inputs[index] && index > 0) {
      // Volta para o campo anterior se apagar
      inputRefs.current[index - 1]?.focus();
    }
  };

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header
        goBack={true}
        title='Verifique seu email'
      />
    );
  };

  const renderDescription = (): JSX.Element => {
    return (
      <text.T16
        style={{
          marginBottom: 24,
          color: theme.colors.textColor,
          paddingRight: 20,
        }}
      >
        Digite seu código OTP aqui.
      </text.T16>
    );
  };

  const renderInputFields = (): JSX.Element => {
    return (
      <div style={{ ...utils.rowCenterSpcBtw(), marginBottom: 30 }}>
        {inputs.map((input, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              width: '17%',
              aspectRatio: 1 / 1,
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 10,
              border: '1px solid #131e48',
              backgroundColor: theme.colors.white,
              margin: 5
            }}
          >
            <input
              ref={(el) => (inputRefs.current[index] = el)}
              maxLength={1}
              style={{
                textAlign: 'center',
                width: '100%',
                height: '100%',
                ...theme.fonts.SourceSansPro_400Regular,
                borderRadius: 600 / 2,
                border: 'none',
                backgroundColor: theme.colors.transparent,
                fontSize: 20,
                color: theme.colors.mainDark,
              }}
              type="text"
              value={input}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderIfDidNotReceiveCode = (): JSX.Element => (
    <div style={{ marginBottom: 10, ...utils.rowCenter() }}>
      <text.T16 style={{ marginRight: 5 }}>
        Não recebeu o código OTP?{' '}
        <button
          onClick={handleResendOtp}
          style={{
            fontSize: 16,
            cursor: 'pointer',
            color: theme.colors.mainColor,
            border: 'none',
            background: 'none'
          }}
        >
          Reenviar
        </button>
      </text.T16>
    </div>
  );

  const renderErrorMessage = (): JSX.Element | null =>
    errorMessage ? (
      <p style={{ color: 'red', marginBottom: 10, textAlign: 'center' }}>
        {errorMessage}
      </p>
    ) : null;


  const renderButton = (): JSX.Element => {
    return (
      <components.Button
        title="Verificar"
        containerStyle={{ marginBottom: 20 }}
        onClick={handleOtpVerification}
      />
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <main style={{flexGrow: 1, padding: 20, marginTop: 52}}>
        {renderDescription()}
        {renderInputFields()}
        {renderIfDidNotReceiveCode()}
        {renderErrorMessage()}
        {renderButton()}
      </main>
    );
  };

  return (
    <div id='screen'>
      {renderBackground()}
      {renderHeader()}
      {renderContent()}
    </div>
  );
};
function setErrorMessage(arg0: string) {
  throw new Error('Function not implemented.');
}

